import { useState, useEffect } from "react";
import { ChevronDown, EyeOff } from "lucide-react";

/**
 * CollapsibleSection — wraps a piece of dashboard content (typically a
 * <Card>) and lets the user collapse it. State persists in localStorage
 * keyed by `id` so the user's choices survive page refreshes.
 *
 * Visual model (no double-card chrome):
 *   - EXPANDED:  child renders unchanged + a tiny "hide" toggle floats
 *                at the top-right corner of the wrapper.
 *   - COLLAPSED: only a compact pill is shown ("▸ Section Name") that
 *                clicks to expand again.
 *
 * The wrapped child is unmounted when collapsed (saves render cycles
 * on heavy cards like AI-synthesis briefs and live charts).
 */
export default function CollapsibleSection({
  id,
  title,
  defaultCollapsed = false,
  children,
}) {
  const storageKey = `niko:collapse:${id}`;

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return defaultCollapsed;
    const saved = localStorage.getItem(storageKey);
    if (saved === "1") return true;
    if (saved === "0") return false;
    return defaultCollapsed;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, collapsed ? "1" : "0");
    } catch (_) {
      /* private mode — silent */
    }
  }, [collapsed, storageKey]);

  // ——— Collapsed state: compact pill only ———
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg
                   border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50
                   text-left text-sm text-zinc-300 hover:text-white transition-colors
                   group"
        data-testid={`collapsible-pill-${id}`}
        aria-expanded="false"
        aria-label={`Expand ${title}`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <ChevronDown className="w-4 h-4 text-zinc-500 group-hover:text-violet-300 flex-shrink-0" />
          <span className="font-medium truncate">{title}</span>
        </span>
        <span className="text-xs text-zinc-500 flex-shrink-0">show</span>
      </button>
    );
  }

  // ——— Expanded state: child + floating hide toggle ———
  return (
    <div className="relative group/collapse" data-testid={`collapsible-${id}`}>
      {children}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setCollapsed(true);
        }}
        className="absolute top-3 right-3 z-10 p-1.5 rounded-md
                   bg-zinc-800/70 hover:bg-zinc-700 text-zinc-400 hover:text-white
                   opacity-50 hover:opacity-100 focus:opacity-100
                   transition-opacity backdrop-blur-sm"
        aria-label={`Hide ${title}`}
        title={`Hide ${title}`}
        data-testid={`collapsible-hide-${id}`}
      >
        <EyeOff className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
